import {
    Set as ISet,
    List,
    memoizeClassMethods,
    OrderedSet,
    Record,
} from '@gitbook/util-immutable';

import { isType, MODEL_TYPES } from '../constants';
import { generateKey } from '../utils';
import { Data } from './data';
import { Leaf, type LeafCreateAttrs } from './leaf';
import type { Mark, MarkProperties } from './mark';
import type { TextRange } from './text-range';

interface TextProperties {
    leaves: List<Leaf>;
    key: string;
}

export type TextCreateProps =
    | Text
    | LeafCreateAttrs
    | Partial<Omit<TextProperties, 'leaves'> & { leaves: List<Leaf> | Array<Leaf> }>;

/*
 * Model for a text node.
 */
export class Text extends Record<TextProperties>({
    leaves: List.create(),
    key: '',
}) {
    public readonly type = 'text';
    public declare readonly key: string;
    public declare readonly leaves: List<Leaf>;

    get object(): 'text' {
        return 'text';
    }

    /*
     * Is the node empty?
     */
    get isEmpty(): boolean {
        return this.text === '';
    }

    /*
     * Get the concatenated text of the node.
     */
    get text(): string {
        return this.getString();
    }

    /*
     * Check if `input` is a `Text`.
     */
    public static isText(input: any): input is Text {
        return isType('TEXT', input);
    }

    /*
     * Check if `input` is a list of texts.
     */
    public static isTextList(input: any): input is List<Text> {
        return List.isList(input) && input.every((item) => Text.isText(item));
    }

    /*
     * Create a new `Text` with `attrs`.
     */
    public static create(attrs: TextCreateProps = ''): Text {
        if (Text.isText(attrs)) {
            return attrs;
        }

        if (typeof attrs === 'string') {
            return Text.createRecord({
                key: generateKey(),
                leaves: List.create([Leaf.create(attrs)]),
            });
        }

        if ('leaves' in attrs) {
            return Text.createRecord({
                key: attrs.key || generateKey(),
                leaves: List.create(attrs.leaves || []),
            });
        }

        return Text.createRecord({
            key: attrs.key || generateKey(),
            leaves: List.create([Leaf.create(attrs)]),
        });
    }

    // Typing
    public isText(): this is Text {
        return true;
    }
    public isBlock(): false {
        return false;
    }
    public isInline(): false {
        return false;
    }

    /**
     * Get the concatenated text of the node, cached for text getter
     */
    public getString(): string {
        return this.leaves.reduce((content, leaf) => content + leaf.text, '');
    }

    /**
     * Implementation of node.getText specific to text nodes.
     */
    public getText(): string {
        return this.getString();
    }

    /*
     * Find the 'first' leaf at offset; By 'first' the alorighthm prefers `endOffset === offset` than `startOffset === offset`
     * Corner Cases:
     *   1. if offset is negative, return the first leaf;
     *   2. if offset is larger than text length, the leaf is null, startOffset, endOffset and index is of the last leaf
     */
    public searchLeafAtOffset(offset: number): {
        startOffset: number;
        endOffset: number;
        index: number;
        leaf: Leaf;
    } {
        let endOffset = 0;
        let startOffset = 0;
        let index = -1;

        const leaf = this.leaves.find((l) => {
            index++;
            startOffset = endOffset;
            endOffset = startOffset + l.text.length;
            return endOffset >= offset;
        });

        return {
            leaf,
            endOffset,
            index,
            startOffset,
        };
    }

    /*
     * Add a `mark` at `index` and `length`.
     */
    public addMark(index: number, length: number, mark: Mark): Text {
        const marks = ISet.create([mark]);
        return this.addMarks(index, length, marks);
    }

    /*
     * Add a `set` of marks at `index` and `length`.
     * Corner Cases:
     *   1. If empty text, and if length === 0 and index === 0, will make sure the text contain an empty leaf with the given mark.
     */
    public addMarks(index: number, length: number, set: ISet<Mark>): Text {
        if (this.text === '' && length === 0 && index === 0) {
            const { leaves } = this;
            const first = leaves.first(null);

            if (!first) {
                return this.set('leaves', List.create([Leaf.create({ text: '', marks: set })]));
            }

            const newFirst = first.addMarks(set);
            if (newFirst === first) {
                return this;
            }
            return this.set('leaves', List.create([newFirst]));
        }

        if (this.text === '') {
            return this;
        }
        if (length === 0) {
            return this;
        }
        if (index >= this.text.length) {
            return this;
        }

        const [before, bundle] = Leaf.splitLeaves(this.leaves, index);
        const [middle, after] = Leaf.splitLeaves(bundle, length);
        return this.setLeaves(
            before.concat(
                middle.map((x) => x.addMarks(set)),
                after
            )
        );
    }

    /*
     * Derive the leaves for a list of `decorations`.
     */
    public getLeaves(
        ranges: TextRange[] | null = [],
        annotateWithRange: boolean | ((range: TextRange) => boolean) = false
    ): List<Leaf> {
        let { leaves } = this;

        if (leaves.size === 0) {
            return List.create([Leaf.create({})]);
        }

        if (!ranges || ranges.length === 0) {
            return leaves;
        }

        if (this.text.length === 0) {
            return leaves;
        }

        const { key } = this;
        ranges.forEach((range) => {
            const { startKey, endKey, startOffset, endOffset, marks } = range;
            const hasStart = startKey === key;
            const hasEnd = endKey === key;
            const annotate =
                typeof annotateWithRange === 'function'
                    ? annotateWithRange(range)
                    : annotateWithRange;

            // Is the range contained into this text?
            if (hasStart || hasEnd) {
                const index = hasStart ? startOffset : 0;
                const length = hasEnd ? endOffset - index : this.text.length - index;

                if (length < 1) {
                    return;
                }

                if (index >= this.text.length) {
                    return;
                }

                if (index !== 0 || length < this.text.length) {
                    const [before, bundle] = Leaf.splitLeaves(leaves, index);
                    const [middle, after] = Leaf.splitLeaves(bundle, length);
                    leaves = before.concat(
                        middle.map((leaf) => {
                            if (marks && marks.size > 0) {
                                leaf = leaf.addMarks(marks);
                            }

                            if (annotate) {
                                leaf = leaf.addSelection(range);
                            }

                            return leaf;
                        }),
                        after
                    );

                    return;
                }
            }

            // Else, we add the marks on the whole leaf
            leaves = leaves.map((leaf) => {
                if (marks && marks.size > 0) {
                    leaf = leaf.addMarks(marks);
                }

                if (annotate) {
                    leaf = leaf.addSelection(range);
                }

                return leaf;
            });
        });

        if (leaves === this.leaves) {
            return leaves;
        }

        return Leaf.createLeaves(leaves);
    }

    /*
     * Derive the leaves for a list of `decorations` and `selections`.
     */
    public getLeavesAndSelections(
        decorations: List<TextRange>,
        selections: List<TextRange>
    ): List<Leaf | TextRange> {
        const decorated = this.getLeaves(decorations.toArray(), false);
        if (selections.size === 0) {
            return decorated;
        }

        let selectedAndDecorated: List<TextRange | Leaf> = this.getLeaves(
            [
                ...selections.toArray().map((selection) =>
                    selection.merge({
                        data: Data.create({ ...selection.data.toObject(), annotate: true }),
                    })
                ),
                ...decorations.toArray(),
            ],
            (range: TextRange) => range.data.get('annotate')
        );

        // Compute where other participants' cursors are positionned
        const { key } = this;
        selections.forEach((selection) => {
            const { focusKey, focusOffset } = selection;

            const hasFocus = focusKey === key;
            if (hasFocus) {
                if (focusOffset !== 0) {
                    const [before, bundle] = Leaf.splitLeaves(
                        selectedAndDecorated as List<Leaf>,
                        focusOffset
                    );

                    selectedAndDecorated = List.create<TextRange | Leaf>([])
                        .concat(before)
                        .push(selection)
                        .concat(bundle);
                } else {
                    selectedAndDecorated = selectedAndDecorated.insert(0, selection);
                }
            }
        });

        return selectedAndDecorated;
    }

    /*
     * Get all of the active marks on between two offsets
     * Corner Cases:
     *   1. if startOffset is equal or bigger than endOffset, then return Set();
     *   2. If no text is selected between start and end, then return Set()
     */
    public getActiveMarksBetweenOffsets(startOffset: number, endOffset: number): ISet<Mark> {
        if (startOffset <= 0 && endOffset >= this.text.length) {
            return this.getActiveMarks();
        }

        if (startOffset >= endOffset) {
            return ISet.create();
        }
        // For empty text in a paragraph, use getActiveMarks;
        if (this.text === '') {
            return this.getActiveMarks();
        }

        let result: null | ISet<Mark> = null;
        let leafEnd = 0;

        this.leaves.forEach((leaf) => {
            const leafStart = leafEnd;
            leafEnd = leafStart + leaf.text.length;

            if (leafEnd <= startOffset) {
                return;
            }
            if (leafStart >= endOffset) {
                return false;
            }

            if (!result) {
                result = leaf.marks;
                return;
            }

            result = result.intersect(leaf.marks);
            if (result && result.size === 0) {
                return false;
            }
            return false;
        });

        return result || ISet.create();
    }

    /**
     * Get all of the active marks on the text.
     */
    public getActiveMarks(): ISet<Mark> {
        if (this.leaves.size === 0) {
            return ISet.create();
        }

        const result = this.leaves.first()!.marks;
        if (result.size === 0) {
            return result;
        }

        return result.withMutations((x) => {
            this.leaves.forEach((leaf) => {
                x.forEach((mark) => {
                    if (!leaf.marks.has(mark)) {
                        const ref = result.deref(mark);
                        if (ref) {
                            x.delete(ref);
                        }
                    }
                });

                if (x.size === 0) {
                    return false;
                }
            });
        });
    }

    /*
     * Get all of the marks on between two offsets
     * Corner Cases:
     *   1. if startOffset is equal or bigger than endOffset, then return Set();
     *   2. If no text is selected between start and end, then return Set()
     */
    public getMarksBetweenOffsets(startOffset: number, endOffset: number): ISet<Mark> {
        if (startOffset <= 0 && endOffset >= this.text.length) {
            return this.getMarks();
        }

        if (startOffset >= endOffset) {
            return ISet.create();
        }
        // For empty text in a paragraph, use getActiveMarks;
        if (this.text === '') {
            return this.getActiveMarks();
        }

        let result: null | ISet<Mark> = null;
        let leafEnd = 0;

        this.leaves.forEach((leaf) => {
            const leafStart = leafEnd;
            leafEnd = leafStart + leaf.text.length;

            if (leafEnd <= startOffset) {
                return;
            }
            if (leafStart >= endOffset) {
                return false;
            }

            if (!result) {
                result = leaf.marks;
                return;
            }

            result = result.union(leaf.marks);
        });

        return result || ISet.create();
    }

    /*
     * Get all of the marks on the text.
     */
    public getMarks(): OrderedSet<Mark> {
        const array = this.getMarksAsArray();
        return OrderedSet.create(array);
    }

    /*
     * Get all of the marks on the text as an array.
     */
    public getMarksAsArray(): Mark[] {
        if (this.leaves.size === 0) {
            return [];
        }
        if (this.leaves.size === 1) {
            return this.leaves.first()!.marks.toArray();
        }

        const result: Mark[] = [];
        this.leaves.forEach((leaf) => {
            result.push(...leaf.marks.toArray());
        });

        return result;
    }

    /**
     * Get the marks on the text at `index`.
     * Corner Cases:
     *   1. if no text is before the index, and index !== 0, then return Set()
     *   2. (for insert after split node or mark at range) if index === 0, and text === '', then return the leaf.marks
     *   3. if index === 0, text !== '', return Set()
     */
    public getMarksAtIndex(index: number): ISet<Mark> {
        const { leaf } = this.searchLeafAtOffset(index);
        if (!leaf) {
            return ISet.create();
        }
        return leaf.marks;
    }

    /**
     * Return true if an index is at the start/end of a leaf.
     */
    public isLeafBoundary(textOffset: number): boolean {
        const { leaf, startOffset, endOffset } = this.searchLeafAtOffset(textOffset);
        if (!leaf) {
            return false;
        }

        return endOffset === textOffset || startOffset === textOffset;
    }

    /*
     * Get a node by `key`, to parallel other nodes.
     */
    public getNode(key: string): Text | null {
        return this.key === key ? this : null;
    }

    /*
     * Check if the node has a node by `key`, to parallel other nodes.
     */
    public hasNode(key: string): boolean {
        return !!this.getNode(key);
    }

    /**
     * Insert `text` at `index`.
     */
    public insertText(offset: number, text: string, marks: ISet<Mark> = ISet.create()): Text {
        if (this.text === '') {
            return this.set('leaves', List.create([Leaf.create({ text, marks })]));
        }

        if (text.length === 0) {
            return this;
        }

        const { startOffset, leaf, index } = this.searchLeafAtOffset(offset);
        const delta = offset - startOffset;
        const beforeText = leaf.text.slice(0, delta);
        const afterText = leaf.text.slice(delta);
        const { leaves } = this;

        if (leaf.marks.equals(marks)) {
            return this.merge({
                leaves: leaves.set(index, leaf.set('text', beforeText + text + afterText)),
            });
        }

        const nextLeaves = leaves.splice(
            index,
            1,
            leaf.set('text', beforeText),
            Leaf.create({ text, marks }),
            leaf.set('text', afterText)
        );

        return this.setLeaves(nextLeaves);
    }

    /**
     * Append text at the end of the text node.
     */
    public appendText(text: string, marks: ISet<Mark> = ISet.create()): Text {
        if (text.length === 0) {
            return this;
        }
        return this.insertText(this.text.length, text, marks);
    }

    /*
     * Regenerate the node's key.
     */
    public regenerateKey(): Text {
        const key = generateKey();
        return this.merge({ key });
    }

    /*
     * Remove a `mark` at `index` and `length`.
     */
    public removeMark(index: number, length: number, mark: Mark): Text {
        if (this.text === '' && index === 0 && length === 0) {
            const first = this.leaves.first(null);
            if (!first) {
                return this;
            }
            const newFirst = first.removeMark(mark);
            if (newFirst === first) {
                return this;
            }
            return this.set('leaves', List.create([newFirst]));
        }

        if (length <= 0) {
            return this;
        }
        if (index >= this.text.length) {
            return this;
        }
        const [before, bundle] = Leaf.splitLeaves(this.leaves, index);
        const [middle, after] = Leaf.splitLeaves(bundle, length);
        const leaves = before.concat(
            middle.map((x) => x.removeMark(mark)),
            after
        );
        return this.setLeaves(leaves);
    }

    /*
     * Remove text from the text node at `start` for `length`.
     */
    public removeText(start: number, length: number): Text {
        if (length <= 0) {
            return this;
        }
        if (start >= this.text.length) {
            return this;
        }

        // PERF: For simple backspace, we can operate directly on the leaf
        if (length === 1) {
            const { leaf, index, startOffset } = this.searchLeafAtOffset(start + 1);
            const offset = start - startOffset;

            if (leaf) {
                if (leaf.text.length === 1) {
                    return this.setLeaves(this.leaves.delete(index));
                }

                const beforeText = leaf.text.slice(0, offset);
                const afterText = leaf.text.slice(offset + length);
                const text = beforeText + afterText;

                if (text.length > 0) {
                    return this.set('leaves', this.leaves.set(index, leaf.set('text', text)));
                }
            }
        }

        const [before, bundle] = Leaf.splitLeaves(this.leaves, start);
        const after = Leaf.splitLeaves(bundle, length)[1];
        const leaves = Leaf.createLeaves(before.concat(after));

        if (leaves.size === 1) {
            const first = leaves.first(null);

            if (first && first.text === '') {
                return this.set('leaves', List.create([first.set('marks', this.getActiveMarks())]));
            }
        }

        return this.merge({ leaves });
    }

    /*
     * Update a `mark` at `index` and `length` with `properties`.
     */
    public updateMark(
        index: number,
        length: number,
        mark: Mark,
        properties: Partial<MarkProperties>
    ): Text {
        const newMark = mark.merge(properties);

        if (this.text === '' && length === 0 && index === 0) {
            const first = this.leaves.first(null);
            if (!first) {
                return this;
            }
            const newFirst = first.updateMark(mark, newMark);
            if (newFirst === first) {
                return this;
            }
            return this.set('leaves', List.create([newFirst]));
        }

        if (length <= 0) {
            return this;
        }
        if (index >= this.text.length) {
            return this;
        }

        const [before, bundle] = Leaf.splitLeaves(this.leaves, index);
        const [middle, after] = Leaf.splitLeaves(bundle, length);

        const leaves = before.concat(
            middle.map((x) => x.updateMark(mark, newMark)),
            after
        );

        return this.setLeaves(leaves);
    }

    /**
     * Map marks in this text. Returning null in the iterator, removes the mark.
     */
    public mapMark(iterator: (mark: Mark) => Mark | null): Text {
        return this.setLeaves(
            this.leaves.map((leaf) => {
                return leaf.merge({
                    marks: leaf.marks.map((mark) => iterator(mark)).filter(Boolean),
                });
            })
        );
    }

    /**
     * Split this text and return two different texts
     */
    public splitText(offset: number): [Text, Text] {
        const splitted = Leaf.splitLeaves(this.leaves, offset);
        const one = this.set('leaves', splitted[0]);
        const two = this.set('leaves', splitted[1]).regenerateKey();
        return [one, two];
    }

    /**
     * Split this text using a delimiter.
     */
    public splitBy(delimiter: string): Text[] {
        const str = this.getString();
        const index = str.indexOf(delimiter);
        if (index === -1) {
            return [this];
        }

        const [one, two] = this.splitText(index);
        return [one, ...two.sliceText(delimiter.length).splitBy(delimiter)];
    }

    /**
     * Slice this text by N chars.
     */
    public sliceText(start: number): Text {
        let offset = start;
        const leaves = this.leaves.map((leaf) => {
            if (offset <= 0) {
                return leaf;
            }

            const toSlice = Math.min(offset, leaf.text.length);
            offset -= toSlice;

            return leaf.merge({
                text: leaf.text.slice(toSlice),
            });
        });

        return this.merge({
            leaves,
        });
    }

    /*
     * merge this text and another text at the end
     */
    public mergeText(text: Text): Text {
        const leaves = this.leaves.concat(text.leaves);
        return this.setLeaves(leaves);
    }

    /*
     * Set leaves with normalized `leaves`.
     */
    public setLeaves(leaves: List<Leaf>): Text {
        const result = Leaf.createLeaves(leaves);

        if (result.size === 1) {
            const first = result.first(null);

            if (!first.marks || first.marks.size === 0) {
                if (first.text === '') {
                    return this.set('leaves', List.create());
                }
            }
        }

        return this.set('leaves', result);
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Text.prototype[MODEL_TYPES.TEXT] = true;

/*
 * Memoize read methods.
 */

memoizeClassMethods(Text, ['getActiveMarks', 'getMarks', 'getMarksAsArray', 'getString']);
